import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, DollarSign, User, TrendingUp, Copy } from 'lucide-react';
import Button from '../ui/Button';
import { formatCurrency } from '../../utils/formatters';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface EmployeeProfile {
  id: string;
  name: string;
  role: string;
  base_salary: number;
  commission_percent?: number;
}

export interface CommissionInvoice {
  id: string;
  number: string;
  total: number;
  sale_type: string;
  client_name: string;
  date: string;
}

export interface CommissionDetails {
  commission_amount: number;
  invoiced_amount: number;
  invoices: CommissionInvoice[];
}

interface CreateSalaryPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (emp: EmployeeProfile, base: number, commission: number) => Promise<void>;
  employee: EmployeeProfile | null;
  periodStart: string;
  periodEnd: string;
  getCommissionDetailsForPeriod: (empId: string) => Promise<CommissionDetails>;
}

const SALE_TYPE_LABELS: Record<string, string> = {
  production: 'Producción',
  express: 'Pedido Express',
  sample: 'Muestra',
  express_sample: 'Muestra Express',
};

const CreateSalaryPreviewModal: React.FC<CreateSalaryPreviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  employee,
  periodStart,
  periodEnd,
  getCommissionDetailsForPeriod,
}) => {
  const [details, setDetails] = useState<CommissionDetails | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && employee) {
      setLoadingPreview(true);
      getCommissionDetailsForPeriod(employee.id).then(setDetails).finally(() => setLoadingPreview(false));
    } else {
      setDetails(null);
    }
  }, [isOpen, employee?.id, getCommissionDetailsForPeriod]);

  const commission = details?.commission_amount ?? 0;

  const base = employee ? (Number(employee.base_salary) || 0) : 0;

  const total = (Number(base) || 0) + (Number(commission) || 0);

  const handleConfirm = async () => {
    if (!employee || total <= 0) return;
    setLoading(true);
    try {
      await onConfirm(employee, Number(base) || 0, Number(commission) || 0);
      onClose();
    } catch (e: any) {
      alert(e.message || 'Error al crear pago');
    } finally {
      setLoading(false);
    }
  };

  const invoicedAmount = details?.invoiced_amount ?? 0;
  const getCommissionPerInvoice = (invTotal: number) =>
    invoicedAmount > 0 ? (invTotal / invoicedAmount) * commission : 0;

  const handleCopyForEmployee = () => {
    const lines: string[] = [
      `Detalle de pago - ${employee.name}`,
      `Período: ${format(new Date(periodStart), 'd MMM yyyy', { locale: es })} – ${format(new Date(periodEnd), 'd MMM yyyy', { locale: es })}`,
      '',
      `Sueldo base: ${formatCurrency(base)}`,
      '',
    ];
    if (employee.role === 'Vendedor') {
      if (details?.invoices?.length) {
        lines.push('Comisiones por cliente:');
        details.invoices.forEach((inv) => {
          const comm = getCommissionPerInvoice(inv.total);
          lines.push(`• ${inv.client_name}: ${formatCurrency(comm)}`);
        });
        lines.push('');
      }
      lines.push(`Comisión total: ${formatCurrency(commission)}`);
    }
    lines.push('');
    lines.push(`Total a cobrar: ${formatCurrency(total)}`);
    const text = lines.join('\n');
    navigator.clipboard.writeText(text).catch(() => {});
  };

  if (!employee) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl overflow-hidden"
        >
          <div className="px-6 pt-6 pb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-indigo-500" />
                Detalle del pago a generar
              </h2>
              <button type="button" onClick={onClose} className="p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                <User className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{employee.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {format(new Date(periodStart), 'd MMM yyyy', { locale: es })} – {format(new Date(periodEnd), 'd MMM yyyy', { locale: es })}
                  </p>
                </div>
              </div>
              {loadingPreview ? (
                <p className="text-sm text-gray-500">Calculando comisiones…</p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Detalle para enviar al colaborador (copiar abajo)</p>
                    <button
                      type="button"
                      onClick={handleCopyForEmployee}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                      title="Copiar detalle: fijo + comisiones a cobrar"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copiar
                    </button>
                  </div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-sm overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-800/90">
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 dark:text-gray-400">Concepto</th>
                          <th className="text-right py-2 px-3 text-xs font-medium text-gray-600 dark:text-gray-400">Importe</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-gray-200 dark:border-gray-700">
                          <td className="py-2 px-3 text-gray-700 dark:text-gray-300">Sueldo base</td>
                          <td className="py-2 px-3 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(base)}</td>
                        </tr>
                        {employee.role === 'Vendedor' && details?.invoices?.map((inv) => (
                          <tr key={inv.id} className="border-t border-gray-200 dark:border-gray-700">
                            <td className="py-2 px-3 text-gray-700 dark:text-gray-300 truncate max-w-[180px]" title={inv.client_name}>
                              {inv.client_name}
                              <span className="block text-xs text-gray-400 dark:text-gray-500">
                                {formatCurrency(inv.total)} fact. · {SALE_TYPE_LABELS[inv.sale_type] || inv.sale_type}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right font-medium text-emerald-600 dark:text-emerald-400">{formatCurrency(getCommissionPerInvoice(inv.total))}</td>
                          </tr>
                        ))}
                        {employee.role === 'Vendedor' && (
                          <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-100/50 dark:bg-gray-800/50">
                            <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                              <span className="inline-flex items-center gap-1">
                                <TrendingUp className="h-4 w-4" /> Comisión total
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right font-semibold text-emerald-700 dark:text-emerald-300">{formatCurrency(commission)}</td>
                          </tr>
                        )}
                        <tr className="border-t-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20">
                          <td className="py-3 px-3 font-semibold text-indigo-700 dark:text-indigo-300">Total a cobrar</td>
                          <td className="py-3 px-3 text-right text-xl font-bold text-indigo-900 dark:text-indigo-100">{formatCurrency(total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">El botón Copiar incluye sueldo base + comisiones por cliente. Lo facturado solo se muestra, no se copia.</p>
                </>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-lg">
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={loading || loadingPreview || total <= 0}
                className="flex-1 rounded-lg"
              >
                {loading ? 'Generando…' : 'Generar pago'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CreateSalaryPreviewModal;
